import React, { useState, useRef } from 'react';
import Layout from '../components/Layout';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate } from 'react-router-dom';

const JOB_TYPES = [
  { value: 'Monolithic Zirconia', label: 'Monolithic Zirconia (5 working days)', days: 5 },
  { value: 'Layered zirconia', label: 'Layered zirconia (7 working days)', days: 7 },
  { value: 'Porcelain bonded crown', label: 'Porcelain bonded crown (7 working days)', days: 7 },
  { value: 'Venirs', label: 'Venirs (10 working days)', days: 10 },
  { value: 'Emax', label: 'Emax (10 working days)', days: 10 },
  { value: 'Composite', label: 'Composite (7 working days)', days: 7 },
  { value: 'Temporary crown Pmma', label: 'Temporary crown Pmma (7 working days)', days: 7 },
  { value: 'Screw retained crown', label: 'Screw retained crown (7 working days)', days: 7 },
  { value: 'Splints', label: 'Splints (5 working days)', days: 5 },
  { value: 'Order', label: 'Order (to discuss)', days: 0 }
];

// Funcție pentru calcularea working days (exclude sâmbăta și duminica)
const addWorkingDays = (startDate: Date, workingDays: number): Date => {
  const result = new Date(startDate);
  let addedDays = 0;
  
  while (addedDays < workingDays) {
    result.setDate(result.getDate() + 1);
    // 0 = Duminică, 6 = Sâmbătă
    if (result.getDay() !== 0 && result.getDay() !== 6) {
      addedDays++;
    }
  }
  
  return result;
};

const CreateOrder = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Stările formularului
  const [orderRef, setOrderRef] = useState('');
  const [jobType, setJobType] = useState('');
  const [description, setDescription] = useState('');
  const [estimatedDate, setEstimatedDate] = useState<Date | null>(null);
  const [shippedBy, setShippedBy] = useState('Royal Mail');
  const [message, setMessage] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Loading și success states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMsg, setShowSuccessMsg] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Validări
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Calculează data estimată când se schimbă job type
  const calculateEstimatedDate = (jobTypeValue: string) => {
  const jobTypeInfo = JOB_TYPES.find(jt => jt.value === jobTypeValue);
  if (jobTypeInfo && jobTypeInfo.days > 0) {
    const currentDate = new Date();
    return addWorkingDays(currentDate, jobTypeInfo.days);
  }
  return null;
};

  // Handler pentru schimbarea job type
  const handleJobTypeChange = (newJobType: string) => {
    setJobType(newJobType);
    const calculatedDate = calculateEstimatedDate(newJobType);
    if (calculatedDate) {
      setEstimatedDate(calculatedDate);
    }
    if (errors.jobType) setErrors(prev => ({ ...prev, jobType: '' }));
  };

  const validateForm = () => {
  const newErrors: Record<string, string> = {};

  if (!orderRef.trim()) {
    newErrors.orderRef = 'Order reference is required';
  }

  if (!jobType) {
    newErrors.jobType = 'Please select a job type';
  }

  if (!estimatedDate) {
    newErrors.estimatedDate = 'Please select an estimated date';
  } else {
    // Corectez validarea datei - compar doar datele, nu și ora
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Resetez ora la începutul zilei
    const selectedDate = new Date(estimatedDate);
    selectedDate.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      newErrors.estimatedDate = 'Estimated date cannot be in the past';
    }
  }

  if (!message.trim()) {
    newErrors.message = 'Please provide a message or description';
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

  // Reset formular după creare
  const resetForm = () => {
    setOrderRef('');
    setJobType('');
    setDescription('');
    setEstimatedDate(null);
    setShippedBy('Royal Mail');
    setMessage('');
    setImageFile(null);
    setImagePreview(null);
    setErrors({});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageChange = (file: File) => {
    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Clear error if there was one
    if (errors.image) {
      setErrors(prev => ({ ...prev, image: '' }));
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, image: 'Image size must be less than 5MB' }));
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, image: 'Please select a valid image file' }));
        return;
      }

      handleImageChange(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleImageChange(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Please login first');
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('orderRef', orderRef.trim());
      formData.append('jobType', jobType);
      formData.append('description', description.trim());
      formData.append('estimatedDate', estimatedDate?.toISOString() || '');
      formData.append('shippedBy', shippedBy);
      formData.append('message', message.trim());
      
      if (imageFile) {
        formData.append('image', imageFile);
        formData.append('imageName', imageFile.name);
      }

      const response = await fetch('https://felixdent-backend.onrender.com/api/orders', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create order');
      }

      // Arată mesaj succes și resetează formularul
      setShowSuccessMsg(true);
      resetForm();

      // După 3 secunde redirecționează
      setTimeout(() => {
        setShowSuccessMsg(false);
        navigate('/my-orders');
      }, 3000);

    } catch (err) {
      console.error('Error creating order:', err);
      alert(`Failed to create order: ${err instanceof Error ? err.message : 'Please try again.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/my-orders')}
            className="mb-4 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-semibold flex items-center gap-2 transition-colors"
            aria-label="Back to My Orders"
          >
            ← Back to My Orders
          </button>

          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Create New Order
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Fill in the details below to create your new order
          </p>
        </div>

        {/* Success Message */}
        {showSuccessMsg && (
          <div className="mb-6 p-4 bg-green-100 dark:bg-green-900 border border-green-300 dark:border-green-700 text-green-800 dark:text-green-200 rounded-lg shadow-sm">
            <div className="flex items-center gap-2">
              <span className="text-xl">✅</span>
              <div>
                <h3 className="font-semibold">Order created successfully!</h3>
                <p className="text-sm">Please check notifications to see updates and order status. Redirecting...</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Order Information Card */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
              📋 Order Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Order Ref */}
              <div>
                <label htmlFor="orderRef" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Order Reference *
                </label>
                <input
                  type="text"
                  id="orderRef"
                  value={orderRef}
                  onChange={(e) => {
                    setOrderRef(e.target.value);
                    if (errors.orderRef) setErrors(prev => ({ ...prev, orderRef: '' }));
                  }}
                  className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    errors.orderRef ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="Enter order reference (e.g., REF-2024-001)"
                />
                {errors.orderRef && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.orderRef}</p>}
              </div>

              {/* Job Type */}
              <div>
                <label htmlFor="jobType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Job Type *
                </label>
                <select
                  id="jobType"
                  value={jobType}
                  onChange={(e) => handleJobTypeChange(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    errors.jobType ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <option value="">Select job type</option>
                  {JOB_TYPES.map((jt) => (
                    <option key={jt.value} value={jt.value}>
                      {jt.label}
                    </option>
                  ))}
                </select>
                {errors.jobType && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.jobType}</p>}
              </div>

              {/* Estimated Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Estimated Completion Date *
                </label>
                <DatePicker
                  selected={estimatedDate}
                  onChange={(date) => {
                    setEstimatedDate(date);
                    if (errors.estimatedDate) setErrors(prev => ({ ...prev, estimatedDate: '' }));
                  }}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select estimated date"
                  minDate={new Date()}
                  className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    errors.estimatedDate ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                {errors.estimatedDate && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.estimatedDate}</p>}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Auto-calculated based on job type selection
                </p>
              </div>

              {/* Shipped By */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Delivery Method
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <input
                      type="radio"
                      name="shippedBy"
                      value="Royal Mail"
                      checked={shippedBy === 'Royal Mail'}
                      onChange={() => setShippedBy('Royal Mail')}
                      className="text-blue-600"
                    />
                    <span className="text-sm">📮 Royal Mail</span>
                  </label>

                  <label className="flex items-center gap-2 p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <input
                      type="radio"
                      name="shippedBy"
                      value="In Person"
                      checked={shippedBy === 'In Person'}
                      onChange={() => setShippedBy('In Person')}
                      className="text-blue-600"
                    />
                    <span className="text-sm">👤 In Person</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              📝 Description
            </h2>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
              placeholder="Enter detailed description of the order requirements..."
            />
          </div>

          {/* Message */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              💬 Message
            </h2>
            <textarea
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                if (errors.message) setErrors(prev => ({ ...prev, message: '' }));
              }}
              rows={4}
              className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none ${
                errors.message ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
              placeholder="Enter your message or special instructions..."
            />
            {errors.message && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.message}</p>}
          </div>

          {/* Image Upload */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              📸 Attach Image (Optional)
            </h2>

            {!imagePreview ? (
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="text-6xl mb-4">📎</div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Drop your image here, or{' '}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
                  >
                    browse
                  </button>
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  PNG, JPG, JPEG up to 5MB
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </div>
            ) : (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full max-w-md mx-auto rounded-lg shadow-sm"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 shadow-sm transition-colors"
                  aria-label="Remove image"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {errors.image && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.image}</p>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/my-orders')}
              className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-8 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors font-medium flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Creating...
                </>
              ) : (
                <>
                  <span>✨</span>
                  Create Order
                </>
              )}
            </button>
          </div>
        </form>
      </main>
    </Layout>
  );
};

export default CreateOrder;